/* @flow @format */

import { createSelector } from 'reselect';
import F from 'lodash/fp';
import * as L from 'partial.lenses';

import { Qdl as ProcQDL } from '@sonyged/ext-proc';

import {
  type Contest,
  type Course,
  getAllCoursesForContest,
  getAllCoursesForContestTypeByCourseType,
  getAllFieldCoursesForContest,
  getAllMissionCoursesForContest,
  getContestByType,
  getIsContestLoading,
  getIsContestStale,
} from './reducer/entities';
import type { ContestType, Selector, StoreState } from './types';
import { getIsRankingLoading } from './reducer/ranking';
import { getProgress } from './reducer/progresses';
import { injectProgresses, injectEntities, injectRanking } from './reducer';

// selectors
// meta

export const isReloadNeeded: Selector<boolean> = state => {
  const ts = state.meta.lastReloaded;
  return Date.now() > ts + 3600000;
};

export const getContests = (state: StoreState) => L.get(['entities', 'contests', 'byId'], state);
export const getContest = (uid: string, state: StoreState) => L.get(['entities', 'contests', 'byId', uid], state);

export type Mission = {
  uid: string,
  qdl: ?string,
  isDefault: boolean,
  title: string,
  quizTitles: string[],
};

// contests
type ContestSelectors = {|
  contestUid: Selector<?string>,
  fieldUid: Selector<?string>,
|};

const makeSelectorsFor = (kind: ContestType): ContestSelectors => {
  const selectContestByType = injectEntities(getContestByType);
  const makeSelectCoursesByType = courseType => state =>
    injectEntities(getAllCoursesForContestTypeByCourseType)(state, { contestType: kind, courseType });

  const contestUid: Selector<?string> = state => {
    const contest = selectContestByType(state, { contestType: kind });
    return contest && contest.uid;
  };

  const selectFieldCourses = makeSelectCoursesByType('field');

  const fieldUid = createSelector(selectFieldCourses, fieldCourses =>
    F.getOr(null, [0, 'courseContents', 0])(fieldCourses)
  );

  return {
    contestUid,
    fieldUid,
  };
};

export const contest: { [ContestType]: ContestSelectors } = {
  ice: makeSelectorsFor('ice'),
  pizza: makeSelectorsFor('pizza'),
  sushi: makeSelectorsFor('sushi'),
  'ice-a': makeSelectorsFor('ice-a'),
  'pizza-a': makeSelectorsFor('pizza-a'),
  'sushi-a': makeSelectorsFor('sushi-a'),
};

export const selectIsContestStale: Selector<boolean> = injectEntities(getIsContestStale);
export const selectIsContestLoading: Selector<boolean> = injectEntities(getIsContestLoading);
export const selectIsRankingLoading: Selector<boolean> = injectRanking(getIsRankingLoading);

export const selectContestByType: Selector<?Contest> = injectEntities(getContestByType);
export const selectAllCoursesForContest: Selector<Array<Course>> = injectEntities(getAllCoursesForContest);
const parser = new ProcQDL.Parser();
const bookCache = {};

export const selectBook = (state: StoreState, uid: string) => {
  try {
    return parser.parse(state.entities.qdls.byId[uid]);
  } catch (_) {
    return null;
  }
};

export type BookInfo = {
  title: string,
  quizTitles: string[],
};

export const selectBookInfo = (state: StoreState, uid: string): BookInfo => {
  if (!bookCache[uid]) bookCache[uid] = selectBook(state, uid);
  const book = bookCache[uid];
  const title = F.get('contents.0.title', book);
  const quizTitles: string[] = F.pipe(F.get('quizzes'), F.map('contents.0.title'))(book);
  return { title, quizTitles };
};

export const selectProgress: Selector<number> = injectProgresses(getProgress);

export const selectAllMissionCoursesForContest: Selector<Array<any>> = (state, contests) => {
  const courses = F.pipe(
    // only keep first courseContent in course
    L.remove([L.elems, 'courseContents', L.offset(1, L.elems)]),
    // add bookinfo
    L.modify([L.elems, 'courseContents', 0], courseContent => ({
      ...courseContent,
      bookInfo: selectBookInfo(state, courseContent.uid),
    })),
    // add isDefault
    L.modify(L.elems, course => ({
      ...course,
      isDefault:
        F.includes('default', course.tagList) || F.includes('default', L.get(['courseContents', 0, 'tagList'], course)),
    }))
  )(injectEntities(getAllMissionCoursesForContest)(state, contests));
  return courses;
};
export const selectAllFieldCoursesForContest: Selector<Array<Course>> = injectEntities(getAllFieldCoursesForContest);
